import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { FileSigned, Pin } from '@factorialco/f0-react/icons/app'
import {
  AvatarNameSelectorDocument,
  CompanyEmployeesCountsDocument,
  CompanyEmployeesTableDocument,
  CompanyEmployeesTableQuery,
  SendForSignatureItAssetDocumentDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useTeamFilter } from 'modules/employees/lib/filters/team'
import EmployeeDetailsSidePanel from 'modules/it_management/components/EmployeeDetailsSidePanel'
import { useItAssetDocumentUpdated } from 'modules/it_management/hooks/useItAssetDocumentUpdated'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

type CompanyEmployee =
  CompanyEmployeesTableQuery['itManagement']['companyEmployeesConnection']['nodes'][number]

interface SelectedItem {
  item: CompanyEmployee
  checked: boolean
}

interface BulkActionParams {
  itemsStatus: readonly SelectedItem[]
}

const PeoplePage = () => {
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const { company, access } = useCurrentSession()
  const client = useApolloClient()
  const teamFilter = useTeamFilter()
  const [sendForSignature] = useMutation(SendForSignatureItAssetDocumentDocument, {
    refetchQueries: [
      { query: CompanyEmployeesTableDocument },
      { query: CompanyEmployeesCountsDocument },
    ],
  })

  const { data: countsData } = useQuery(CompanyEmployeesCountsDocument, {
    fetchPolicy: 'cache-and-network',
  })

  useItAssetDocumentUpdated()

  useEffect(() => {
    track(trackingEvents.itManagement.itAssetDocument.goToPeopleTab, {
      company_id: company.id,
      user_id: access.id,
    })
  }, [company.id, access.id])

  const [peopleCounts, setPeopleCounts] = useState({
    all: 0,
    active: 0,
    newHires: 0,
    terminated: 0,
  })

  const [selectedEmployee, setSelectedEmployee] = useState<CompanyEmployee | null>(null)

  useEffect(() => {
    if (!countsData) return
    setPeopleCounts({
      all: countsData.itManagement.allCount.totalCount ?? 0,
      active: countsData.itManagement.activeCount.totalCount ?? 0,
      newHires: countsData.itManagement.newHiresCount.totalCount ?? 0,
      terminated: countsData.itManagement.terminatedCount.totalCount ?? 0,
    })
  }, [countsData])

  const handleSendForSignature = useCallback(
    async (employeeIds: number[], origin: '3 dots option' | 'bulk action') => {
      if (!company?.id || employeeIds.length === 0) return

      try {
        const result = await sendForSignature({
          variables: {
            companyId: Number(company.id),
            employeeIds: employeeIds,
          },
        })

        const itAssetDocuments =
          result.data?.itManagementMutations?.sendForSignatureItmanagementItAssetDocument
            ?.itAssetDocuments || []

        const uniqueDocuments = new Set(
          itAssetDocuments
            .map((doc) => `${doc.employeeId}-${doc.documentType}`)
            .filter((key) => !key.includes('undefined') && !key.includes('null'))
        )

        uniqueDocuments.forEach(() => {
          track(trackingEvents.itManagement.itAssetDocument.sendDocumentForSignature, {
            company_id: company.id,
            user_id: access.id,
            origin,
          })
        })

        app.setFlash({
          type: 'correct',
          message: i18n.t('it_management.people.send_signature.success'),
        })
      } catch (error) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('it_management.people.send_signature.error'),
        })
      }
    },
    [company?.id, access.id, sendForSignature]
  )

  const handleCloseSidePanel = useCallback(() => {
    setSelectedEmployee(null)
  }, [])

  const dataSourceOptions = useMemo(() => {
    const definedFilters = {
      documentsSigned: {
        type: 'in' as const,
        label: i18n.t('it_management.people.filters.documents_signed'),
        options: {
          options: async () => [
            {
              label: i18n.t('it_management.people.filters.documents_signed_options.missing'),
              value: 'missing',
            },
            {
              label: i18n.t('it_management.people.filters.documents_signed_options.complete'),
              value: 'complete',
            },
          ],
        },
      },
      startDate: {
        type: 'in' as const,
        label: i18n.t('it_management.people.filters.start_date'),
        options: {
          options: async () => [
            {
              label: i18n.t('it_management.people.filters.start_date_options.in_30_days'),
              value: 'in_30_days',
            },
            {
              label: i18n.t('it_management.people.filters.start_date_options.started_30_days'),
              value: 'started_30_days',
            },
            {
              label: i18n.t(
                'it_management.people.filters.start_date_options.started_more_than_30_days'
              ),
              value: 'started_more_than_30_days',
            },
          ],
        },
      },
      team: teamFilter,
      manager: {
        type: 'in' as const,
        label: i18n.t('employees.employees_list.filters.reporting_to'),
        options: {
          cache: true,
          options: async () => {
            if (!company?.id) return []

            const { data } = await client.query({
              query: AvatarNameSelectorDocument,
              variables: {
                companyId: Number(company.id),
                includeTeams: false,
                includeWorkplaces: false,
              },
              fetchPolicy: 'cache-first',
            })

            return data.employees.employeeNames.map((employee) => ({
              label: employee.fullName,
              value: employee.id,
            }))
          },
        },
      },
      employeeStatus: {
        type: 'in' as const,
        label: i18n.t('it_management.people.filters.employee_status'),
        options: {
          options: async () => [
            {
              label: i18n.t('it_management.people.presets.active_employees'),
              value: 'active',
            },
            {
              label: i18n.t('it_management.people.presets.new_hires'),
              value: 'newHires',
            },
            {
              label: i18n.t('it_management.people.presets.offboarded'),
              value: 'terminated',
            },
          ],
        },
      },
    }

    return {
      document: CompanyEmployeesTableDocument,
      mapData: (tableData: CompanyEmployeesTableQuery) =>
        tableData.itManagement.companyEmployeesConnection.nodes,
      perPage: 25,
      itemOnClick: (item: CompanyEmployee) => () => {
        setSelectedEmployee(item)
      },
      sortings: {
        employeeName: {
          label: i18n.t('it_management.common.people_title'),
          sortBy: 'employeeName',
        },
        startDate: {
          label: i18n.t('it_management.common.start_date'),
          sortBy: 'startDate',
        },
        workplace: {
          label: i18n.t('it_management.common.workplace'),
          sortBy: 'workplace',
        },
      },
      defaultSortings: {
        field: 'employeeName' as const,
        order: 'asc' as const,
      },
      search: {
        enabled: true,
      },
      filters: definedFilters,
      presets: [
        {
          label: i18n.t('it_management.people.presets.active_employees'),
          filter: {
            employeeStatus: ['active'],
          },
          itemsCount: () => peopleCounts.active,
        },
        {
          label: i18n.t('it_management.people.presets.new_hires'),
          filter: {
            employeeStatus: ['newHires'],
          },
          itemsCount: () => peopleCounts.newHires,
        },
        {
          label: i18n.t('it_management.people.presets.offboarded'),
          filter: {
            employeeStatus: ['terminated'],
          },
          itemsCount: () => peopleCounts.terminated,
        },
      ],
      mapVariables: (variables: {
        search?: string
        sortings?: Array<{ field: string; order: string }>
        filters?: Record<string, unknown>
      }) => {
        const filters = variables.filters || {}

        const employeeStatus: string[] | undefined = Array.isArray(filters.employeeStatus)
          ? filters.employeeStatus
          : undefined
        const onlyActive = employeeStatus?.includes('active') ?? false
        const onlyNewHires = employeeStatus?.includes('newHires') ?? false
        const onlyTerminated = employeeStatus?.includes('terminated') ?? false

        // Only process filters that are defined in the filters definition
        const definedFilterKeys = Object.keys(definedFilters)
        const normalizedFilters: Record<string, unknown> = Object.keys(filters)
          .filter((key) => definedFilterKeys.includes(key))
          .reduce<Record<string, unknown>>((acc, key) => {
            acc[key] = filters[key]
            return acc
          }, {})

        const documentsSigned: string[] | undefined = Array.isArray(
          normalizedFilters.documentsSigned
        )
          ? normalizedFilters.documentsSigned
          : undefined
        const onlyPendingDocuments =
          documentsSigned?.includes('missing') && !documentsSigned?.includes('complete')
            ? true
            : documentsSigned?.includes('complete') && !documentsSigned?.includes('missing')
              ? false
              : undefined

        const startDate: string[] | undefined = Array.isArray(normalizedFilters.startDate)
          ? normalizedFilters.startDate
          : undefined
        const startDateFilter = startDate && startDate.length === 1 ? startDate[0] : undefined

        const teamIds: number[] | undefined = Array.isArray(normalizedFilters.team)
          ? normalizedFilters.team.map((id) => Number(id)).filter((id) => !isNaN(id))
          : undefined

        const managerIds: number[] | undefined = Array.isArray(normalizedFilters.manager)
          ? normalizedFilters.manager.map((id) => Number(id)).filter((id) => !isNaN(id))
          : undefined

        return {
          search: variables.search,
          sort: variables.sortings?.[0],
          onlyActive,
          onlyTerminated,
          onlyNewHires,
          onlyPendingDocuments: onlyPendingDocuments,
          startDateFilter: startDateFilter,
          teamIds: teamIds && teamIds.length > 0 ? teamIds : undefined,
          managerIds: managerIds && managerIds.length > 0 ? managerIds : undefined,
        }
      },
      mapPagination: (tableData: CompanyEmployeesTableQuery) => ({
        total: tableData.itManagement.companyEmployeesConnection.totalCount ?? 0,
      }),
      selectable: (item: CompanyEmployee) => item.id,
      bulkActions: (selectedItems: BulkActionParams) => {
        const selectedCheckedItems = selectedItems.itemsStatus.filter((item) => item.checked)
        const hasDraftDocuments = selectedCheckedItems.some(
          (item) => item.item.hasDraftDocuments ?? false
        )

        if (!hasDraftDocuments || selectedCheckedItems.length === 0) {
          return { primary: [], secondary: [] }
        }

        return {
          primary: [
            {
              id: 'send-it-documents',
              label: i18n.t('it_management.people.bulk_actions.send_it_documents'),
              icon: FileSigned,
            },
          ],
          secondary: [],
        }
      },
      itemActions: (item: CompanyEmployee) => {
        const actions = []

        if (item.hasDraftDocuments ?? false) {
          actions.push({
            label: i18n.t('it_management.people.actions.send_for_signature'),
            icon: FileSigned,
            onClick: () => {
              handleSendForSignature([item.id], '3 dots option')
            },
          })
        }

        return actions
      },
    }
  }, [handleSendForSignature, peopleCounts, client, company?.id, teamFilter])

  const source = useGraphqlDataCollectionSource(dataSourceOptions, [])

  return (
    <>
      <ResponsivePage.Body
        authorized={isItManagementEnabled}
        error={featureError}
        loading={featureLoading}
        variant="full"
      >
        <Box id="it-management-people-data-collection">
          <OneDataCollection
            source={source}
            emptyStates={{
              'no-data': {
                emoji: '👥',
                title: i18n.t('it_management.people.empty_state.title'),
                description: i18n.t('it_management.people.empty_state.description'),
              },
            }}
            onBulkAction={(action: string, selectedItems: BulkActionParams) => {
              const selectedCheckedItems = selectedItems.itemsStatus.filter((item) => item.checked)
              const employeeIds = selectedCheckedItems.map((itemStatus) => itemStatus.item.id)

              switch (action) {
                case 'send-it-documents':
                  handleSendForSignature(employeeIds, 'bulk action')
                  break
              }
            }}
            visualizations={[
              {
                type: 'table',
                options: {
                  columns: [
                    {
                      label: i18n.t('it_management.common.people_title'),
                      render: (item: CompanyEmployee) => {
                        const firstName = typeof item.firstName === 'string' ? item.firstName : ''
                        const lastName = typeof item.lastName === 'string' ? item.lastName : ''

                        if (
                          (!firstName || firstName === '') &&
                          (!lastName || lastName === '') &&
                          item.employeeName
                        ) {
                          const nameParts = item.employeeName.trim().split(/\s+/)
                          return {
                            type: 'person',
                            value: {
                              firstName: nameParts[0] ?? '',
                              lastName: nameParts.slice(1).join(' ') ?? '',
                              src: buildUrl(item.access?.avatar),
                            },
                          }
                        }

                        return {
                          type: 'person',
                          value: {
                            firstName: firstName ?? '',
                            lastName: lastName ?? '',
                            src: buildUrl(item.access?.avatar),
                          },
                        }
                      },
                      sorting: 'employeeName',
                    },
                    {
                      label: i18n.t('it_management.common.start_date'),
                      render: (item: CompanyEmployee) => ({
                        type: 'text',
                        value: item.startDate ? dateBackendDateTimeToUI(item.startDate) : '-',
                      }),
                      sorting: 'startDate',
                    },
                    {
                      label: i18n.t('it_management.common.workplace'),
                      render: (item: CompanyEmployee) => {
                        if (!item.workplace) {
                          return {
                            type: 'text',
                            value: '-',
                          }
                        }
                        return {
                          type: 'tag',
                          value: {
                            label: item.workplace,
                            icon: Pin,
                          },
                        }
                      },
                      sorting: 'workplace',
                    },
                    {
                      label: i18n.t('it_management.common.total_it_assets'),
                      render: (item: CompanyEmployee) => ({
                        type: 'tag',
                        value: {
                          label: item.totalItAssets.toString(),
                        },
                      }),
                    },
                    {
                      label: i18n.t('it_management.common.pending_signatures'),
                      render: (item: CompanyEmployee) => {
                        const hasDraftDocuments = item.hasDraftDocuments ?? false
                        const hasPendingSignatureDocuments =
                          item.hasPendingSignatureDocuments ?? false
                        const hasCompletedDocuments = item.hasCompletedDocuments ?? false

                        if (hasPendingSignatureDocuments) {
                          return {
                            type: 'status',
                            value: {
                              status: 'warning',
                              label: i18n.t('it_management.common.pending'),
                            },
                          }
                        }

                        if (hasDraftDocuments) {
                          return {
                            type: 'status',
                            value: {
                              status: 'neutral',
                              label: i18n.t('it_management.common.draft'),
                            },
                          }
                        }

                        if (hasCompletedDocuments) {
                          return {
                            type: 'status',
                            value: {
                              status: 'positive',
                              label: i18n.t('it_management.common.completed'),
                            },
                          }
                        }

                        return {
                          type: 'status',
                          value: {
                            label: '—',
                          },
                        }
                      },
                    },
                  ],
                },
              },
            ]}
          />
        </Box>
      </ResponsivePage.Body>

      {selectedEmployee && (
        <EmployeeDetailsSidePanel
          employee={selectedEmployee}
          open={!!selectedEmployee}
          onClose={handleCloseSidePanel}
        />
      )}
    </>
  )
}

export default PeoplePage
